/**
 * Formatting Utilities for Buyer App
 */

import { format, formatDistanceToNow, parseISO } from 'date-fns';

/**
 * Parse timestamp ensuring UTC interpretation
 * Backend stores timestamps in UTC without 'Z' suffix, ensure they're properly parsed as UTC
 */
function parseUTCTimestamp(timestamp: string): Date {
  // If timestamp doesn't have timezone info, assume UTC and append 'Z'
  if (!timestamp.endsWith('Z') && !timestamp.includes('+') && !timestamp.match(/-\d{2}:\d{2}$/)) {
    return new Date(timestamp + 'Z');
  }
  return parseISO(timestamp);
}

/**
 * Format currency (PHP)
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency: 'PHP',
    minimumFractionDigits: 2,
  }).format(amount);
}

/**
 * Format time (relative or absolute) - properly handles UTC timestamps
 */
export function formatTime(timestamp: string): string {
  try {
    // Parse as UTC (backend timestamps are in UTC)
    const utcDate = parseUTCTimestamp(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - utcDate.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      // For relative time, formatDistanceToNow uses current locale time correctly
      return formatDistanceToNow(utcDate, { addSuffix: true });
    } else {
      // For absolute time, format in user's local timezone (which should be PH)
      return format(utcDate, 'MMM d, yyyy h:mm a');
    }
  } catch {
    return timestamp;
  }
}

/**
 * Format absolute time
 */
export function formatAbsoluteTime(timestamp: string): string {
  try {
    const date = parseISO(timestamp);
    return format(date, 'h:mm a');
  } catch {
    return timestamp;
  }
}

/**
 * Format duration (minutes to readable)
 */
export function formatDuration(minutes: number): string {
  if (minutes < 60) {
    return `${minutes} min`;
  }
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

/**
 * Format ETA range
 */
export function formatETARange(minMinutes: number, maxMinutes: number): string {
  return `${minMinutes}-${maxMinutes} min`;
}

/**
 * Format percentage
 */
export function formatPercentage(value: number, decimals = 1): string {
  return `${value.toFixed(decimals)}%`;
}

/**
 * Format order ID (show last 6 characters)
 */
export function formatOrderId(orderId: string): string {
  if (orderId.length <= 8) return orderId;
  return `#${orderId.substring(orderId.length - 6).toUpperCase()}`;
}

/**
 * Mask phone number (show last 4 digits)
 */
export function maskPhone(phone: string): string {
  if (phone.length < 4) return phone;
  return `**** ${phone.substring(phone.length - 4)}`;
}

/**
 * Format number with commas
 */
export function formatNumber(num: number): string {
  return new Intl.NumberFormat('en-US').format(num);
}

/**
 * Format distance (meters to readable)
 */
export function formatDistance(meters: number): string {
  if (meters < 1000) {
    return `${Math.round(meters)}m`;
  }
  const km = meters / 1000;
  return `${km.toFixed(1)}km`;
}

/**
 * Format promo discount
 */
export function formatPromoDiscount(kind: 'percentage' | 'fixed_amount' | 'free_delivery', value: number): string {
  switch (kind) {
    case 'percentage':
      return `${value}% OFF`;
    case 'fixed_amount':
      return `₱${value} OFF`;
    case 'free_delivery':
      return 'FREE DELIVERY';
    default:
      return '';
  }
}















